import { formatSeconds } from '@/local/server-data/utils/smartUtils/duration'
import { parseOrderStepId } from '../../../utils/orders'

type TransportExtOrderStepLike =
  | uui.domain.client.rm.TransportExtendedOrderStep
  | uui.domain.client.rm.TransportExtendedOrderStepWithoutFormattedData

type BarcodesByStatus = Record<
  uui.domain.server.rm.BarcodePod['barcodeStatus'],
  uui.domain.server.rm.BarcodePod[]
>

const PRIORITY_INTL_MAP_ID: Record<string, uui.domain.intl.LocalizationIdentifier> = {
  lowest: 'orders.priority.lowest',
  lower: 'orders.priority.lower',
  normal: 'orders.priority.normal',
  higher: 'orders.priority.higher',
  highest: 'orders.priority.highest',
}

const PRIORITY_MAP_BY_VALUE = {
  [-20]: 'lowest',
  [-10]: 'lower',
  [0]: 'normal',
  [10]: 'higher',
  [20]: 'highest',
}

const statusIntlMap: {
  [key in uui.domain.server.LocationStatus]: uui.domain.intl.LocalizationIdentifier
} = {
  OK: 'order.location.status.ok',

  // LOW ACCURACY
  LOW_ACCURACY: 'order.location.status.lowAccuracy',
  POSTCODE_ISSUE: 'order.location.status.postcodeIssue',
  POSTCODE_LEVEL_ACCURACY: 'order.location.status.postcodeAccuracy',

  // OUTSIDE TERRITORY LIMITS
  OUT_OF_TERRITORY_RANGE: 'order.location.status.outOfTerritoryRange',

  // ERROR
  NOT_FOUND: 'order.location.status.geocodingError',
  GEOCODING_ERROR: 'order.location.status.geocodingError',
  GEOCODING_TIMEOUT: 'order.location.status.geocodingError',
  GEOCODING_FAILED: 'order.location.status.geocodingError',
}

type OrderStepStatus = 'assigned' | 'executed' | 'reschedule' | 'done' | 'unassigned'
export const orderStepStatusIntlId: Record<
  OrderStepStatus,
  uui.domain.intl.LocalizationIdentifier
> = {
  assigned: 'global.assigned',
  executed: 'orders.stepTrackingDataStatus.executed',
  reschedule: 'orders.stepTrackingDataStatus.reschedule',
  done: 'orders.stepTrackingDataStatus.done',
  unassigned: 'global.unassigned',
}

function getStatusIntlId(
  status: uui.domain.server.LocationStatus,
): uui.domain.intl.LocalizationIdentifier {
  return statusIntlMap[status]
}

function getTagsAsString(tags: string[], separator: string = ','): string {
  let acc = ''

  tags.forEach((tag, index) => {
    acc += tag

    if (separator && index < tags.length - 1) {
      acc += `${separator}`
    }
  })

  return acc
}

function getAssignedVehicleRawData(extOrderStep: TransportExtOrderStepLike) {
  if (extOrderStep.isUnassigned || !extOrderStep.hasVehicle) return

  const { vehicleName = '', dateAsString = '' } = extOrderStep
  if (vehicleName === '' || dateAsString === '') return

  return {
    vehicle: vehicleName,
    assigned: true,
    dateAsString,
  }
}

function getBarcodesByStatus(podArray: uui.domain.client.rm.BarcodePod[]): BarcodesByStatus {
  const barcodesByStatusInitialValue: BarcodesByStatus = {
    SCANNED: [],
    UNREADABLE: [],
    MISSING_BARCODE: [],
    MISSING_PACKAGE: [],
  }

  if (!podArray || podArray.length === 0) {
    return barcodesByStatusInitialValue
  }

  return podArray.reduce((acc, pod) => {
    if (!acc[pod.barcodeStatus]) {
      acc[pod.barcodeStatus] = []
    }

    acc[pod.barcodeStatus].push(pod)

    return acc
  }, barcodesByStatusInitialValue)
}

function getAddressCheck(
  extOrderStep: TransportExtOrderStepLike,
  translate: uui.domain.intl.Translate,
): string {
  const {
    location: { status = 'OK' } = {},
  } = extOrderStep

  const orderStatus = status || 'OK'

  return translate({ id: getStatusIntlId(orderStatus) })
}

function getRequiredTags(orderStep: uui.domain.client.rm.OrderStep): string {
  const { tagsIn } = orderStep

  return tagsIn && tagsIn.length > 0 ? getTagsAsString(tagsIn) : '-'
}

function getBannedTags(orderStep: uui.domain.client.rm.OrderStep): string {
  const { tagsOut } = orderStep

  return tagsOut && tagsOut.length > 0 ? getTagsAsString(tagsOut) : '-'
}

function getTimeWindow(orderStep: uui.domain.client.rm.OrderStep, index: number) {
  const { timeWindows } = orderStep

  const tw: uui.domain.rm.TimeWindow | undefined = timeWindows?.[index]
    ? timeWindows[index]
    : undefined

  return tw
}

function getPriority(
  order: uui.domain.client.rm.Order,
  translate: uui.domain.intl.Translate,
): string {
  const { priority } = order

  return translate({ id: PRIORITY_INTL_MAP_ID[PRIORITY_MAP_BY_VALUE[priority]] })
}

function getBarcodes(
  extOrderStep: TransportExtOrderStepLike,
  orderStep: uui.domain.client.rm.OrderStep,
): string {
  const { barcodes: totalBarcodes, trackingData } = orderStep

  const barcodesCount = totalBarcodes.length

  if (!extOrderStep.isUnassigned && !extOrderStep.isRouteApproved) {
    return barcodesCount > 0 ? `${barcodesCount}` : '-'
  }

  let scannedCount = 0
  let scannedDriverBarcodesCount = 0

  if (trackingData) {
    const {
      pods: { barcodes, driverBarcodes },
    } = trackingData

    if (!barcodes && !driverBarcodes) return '-'

    if (barcodes) {
      scannedCount = getBarcodesByStatus(barcodes).SCANNED.length
    }

    if (driverBarcodes) {
      scannedDriverBarcodesCount = getBarcodesByStatus(driverBarcodes).SCANNED.length
    }
  }

  const baseFormat = `${scannedCount}/${barcodesCount} Scanned`

  if (scannedDriverBarcodesCount > 0) {
    return `${baseFormat} + ${scannedDriverBarcodesCount} (new)`
  }

  return baseFormat
}

function getIdleTime(
  extOrderStep: TransportExtOrderStepLike,
  translate: uui.domain.intl.Translate,
): string {
  if (extOrderStep.isUnassigned) return '-'

  const { idleTimeSec } = extOrderStep

  return idleTimeSec ? formatSeconds(translate)(idleTimeSec, 'SHORT') : '-'
}

function getRequiredVehicle(
  order: uui.domain.client.rm.Order,
  vehiclesMap: Record<string, uui.domain.client.rm.Vehicle>,
): string {
  const { forceVehicleId } = order

  if (forceVehicleId) {
    const vehicle = vehiclesMap[forceVehicleId]

    if (vehicle) return vehicle.externalId
  }

  return '-'
}

function getAssignedVehicle(extOrderStep: TransportExtOrderStepLike) {
  if (extOrderStep.isUnassigned) return undefined

  const assignedData = getAssignedVehicleRawData(extOrderStep)

  return assignedData
    ? {
        dateAsString: assignedData.dateAsString,
        vehicle: assignedData.vehicle,
      }
    : undefined
}

function getScheduleTime(extOrderStep: TransportExtOrderStepLike): number {
  if (extOrderStep.isUnassigned) return Number.NEGATIVE_INFINITY

  return extOrderStep.stepStartSec
}

function getServiceTime(
  orderStep: uui.domain.client.rm.OrderStep,
  translate: uui.domain.intl.Translate,
): string {
  const { serviceTimeSec } = orderStep

  return formatSeconds(translate)(serviceTimeSec, 'SHORT')
}

function getOrderType(
  extOrder: TransportExtOrderStepLike,
  translate: uui.domain.intl.Translate,
): string {
  const { type, orderStepType } = extOrder
  const typeToCheck = type === 'pd' ? orderStepType : type

  switch (typeToCheck) {
    case 'p':
      return translate({ id: 'global.pickup' })
    case 'd':
      return translate({ id: 'global.delivery' })
    case 's':
      return translate({ id: 'global.service' })
    default:
      return '-'
  }
}

const getOrderStepStatusReason = (orderStep: uui.domain.client.rm.OrderStep): string => {
  const { trackingData } = orderStep
  return trackingData?.statusReason ?? ''
}

function getOrderStep(extOrderStep: TransportExtOrderStepLike, order: uui.domain.client.rm.Order) {
  switch (order.type) {
    case 'p':
      return order.pickup

    case 's':
    case 'd':
      return order.delivery

    case 'pd':
      return extOrderStep.orderStepType === 'p' ? order.pickup : order.delivery
  }
}

const getOrderStepStatus = (
  extOrderStep: TransportExtOrderStepLike,
  orderStep: uui.domain.client.rm.OrderStep,
  translate: uui.domain.intl.Translate,
): string => {
  const { trackingData } = orderStep

  let orderStepStatus = 'unassigned'

  if (!extOrderStep.isUnassigned) {
    orderStepStatus = extOrderStep.hasBeenExecuted ? 'executed' : 'assigned'
  }

  if (!!trackingData) {
    const { status } = trackingData

    if (status) {
      if (status === 'done') {
        orderStepStatus = 'done'
      } else if (status === 'reschedule') {
        orderStepStatus = 'reschedule'
      }
    }
  }

  const orderStatusReason = getOrderStepStatusReason(orderStep)
  const orderStatus = translate({ id: orderStepStatusIntlId[orderStepStatus] })

  return orderStatusReason && orderStatusReason.length > 0
    ? `${orderStatus} (${orderStatusReason})`
    : orderStatus
}

function getAddress(
  extOrderStep:
    | uui.domain.client.rm.TransportExtendedOrderStep
    | uui.domain.client.rm.TransportExtendedOrderStepWithoutFormattedData,
) {
  if (extOrderStep.atDepot) return extOrderStep.depot.name

  return extOrderStep.location.address
}

function getNotes(orderStep: uui.domain.client.rm.OrderStep) {
  return orderStep.notes ?? '-'
}

function getPhone(orderStep: uui.domain.client.rm.OrderStep) {
  return orderStep.phone ?? '-'
}

function getEmail(orderStep: uui.domain.client.rm.OrderStep) {
  return orderStep.email ?? '-'
}

function getCustomFields(orderStep: uui.domain.client.rm.OrderStep) {
  return orderStep.customFields
}

function getShopifyOrderName(order: uui.domain.client.rm.Order) {
  return order?.integrationData?.integrationType === 'SHOPIFY'
    ? order.integrationData.shopifyName
    : '-'
}

function getStopNumber(extOrderStep: TransportExtOrderStepLike) {
  if (extOrderStep.isUnassigned) return '-'
  if (extOrderStep.stepNumber === 0) return ''

  return `${extOrderStep.stepNumber}`
}

function getPairId(extOrderStep: TransportExtOrderStepLike) {
  if (extOrderStep.type !== 'pd') return '-'
  return extOrderStep.pairedIdLabel
}

function getExecutedLoads(
  loads: Record<string, number>,
  loadTrackedMap?: Record<string, uui.domain.server.rm.TrackedLoad>,
) {
  const executedLoads: Record<string, number> = Object.keys(loads).reduce((acc, curr) => {
    acc[curr] = null
    return acc
  }, {})

  if (!loadTrackedMap) return executedLoads

  for (const loadId of Object.keys(loadTrackedMap)) {
    executedLoads[loadId] = loadTrackedMap[loadId].value
  }

  return executedLoads
}

function getExecutedCustomFields(
  customFields: Record<string, string | null>,
  customFieldTrackedMap?: Record<string, uui.domain.server.rm.TrackedCustomField>,
) {
  const executedCustomFields: Record<string, string> = Object.keys(customFields).reduce(
    (acc, curr) => {
      acc[curr] = null
      return acc
    },
    {},
  )

  if (!customFieldTrackedMap) return executedCustomFields

  for (const customFieldId of Object.keys(customFieldTrackedMap)) {
    executedCustomFields[customFieldId] = customFieldTrackedMap[customFieldId].value
  }

  return executedCustomFields
}

function getPlannedTime(
  extOrderStep: TransportExtOrderStepLike,
  approvedPlans: uui.domain.client.rm.ApprovedPlans,
): number {
  if (extOrderStep.isUnassigned) return Number.NEGATIVE_INFINITY

  const approvedPlan = approvedPlans[extOrderStep.dateAsString]
  if (!approvedPlan) return Number.NEGATIVE_INFINITY

  const approvedRoute =
    approvedPlan.approvedRoutes[extOrderStep.routeId.replace(`-${extOrderStep.dateAsString}`, '')]

  if (!approvedRoute) return Number.NEGATIVE_INFINITY

  const { orderId, orderStepType } = parseOrderStepId(extOrderStep.id)
  const type = orderStepType === 'p' ? 'pickup' : 'delivery'
  const approvedStep = approvedRoute.route.steps.find(
    step => step.ref === orderId && step.type === type,
  )
  if (!approvedStep) return Number.NEGATIVE_INFINITY

  return approvedStep.startSec
}

function getCommunicatedEtaRanges(
  orderId: string,
  type: uui.domain.client.rm.OrderStepIdentifier,
  orderStepTrackingDataRegistry: Record<string, uui.domain.server.rm.GlobalOrderStepTrackingData>,
): uui.domain.client.rm.CommunicatedEtaRanges {
  const entryType = type === 'd' ? 'delivery' : type === 's' ? 'service' : 'pickup'
  const entryId = `${entryType}-${orderId}`
  const entry = orderStepTrackingDataRegistry[entryId]

  const communicatedEtaRanges: uui.domain.client.rm.CommunicatedEtaRanges = {
    twoDaysNotification: undefined,
    oneDayNotification: undefined,
    sameDayNotification: undefined,
  }

  if (!entry) {
    return communicatedEtaRanges
  }
  const { notificationData } = entry

  const keys = Object.keys(notificationData).sort()

  for (const key of keys) {
    const data = notificationData[key]

    if (
      data.advanceNotificationTwoDaySent &&
      data.advanceNotificationTwoDaySent !== 'NONE' &&
      data.lastEtaRangeSent2Days
    ) {
      communicatedEtaRanges.twoDaysNotification = data.lastEtaRangeSent2Days
    }

    if (
      data.advanceNotificationOneDaySent &&
      data.advanceNotificationOneDaySent !== 'NONE' &&
      data.lastEtaRangeSent1Day
    ) {
      communicatedEtaRanges.oneDayNotification = data.lastEtaRangeSent1Day
    }

    if (data.bodSent && data.bodSent !== 'NONE' && data.lastEtaRangeSent) {
      communicatedEtaRanges.sameDayNotification = data.lastEtaRangeSent
    }
  }

  return communicatedEtaRanges
}

export function getRecurrence(
  recurrencesMap: Record<string, uui.domain.client.rm.Recurrence>,
  recurrenceId?: string,
): uui.domain.client.rm.Recurrence | undefined {
  if (!recurrenceId) return undefined
  return recurrencesMap[recurrenceId]
}

export function generateFormattedData(
  extOrderStep: TransportExtOrderStepLike,
  order: uui.domain.client.rm.Order,
  vehiclesMap: Record<string, uui.domain.client.rm.Vehicle>,
  approvedPlans: uui.domain.client.rm.ApprovedPlans,
  _: uui.domain.UserConfiguration,
  orderStepTrackingDataRegistry: Record<string, uui.domain.server.rm.GlobalOrderStepTrackingData>,
  recurrencesMap: Record<string, uui.domain.client.rm.Recurrence>,
  translate: uui.domain.intl.Translate,
): uui.domain.client.rm.FormattedData {
  const orderStep = getOrderStep(extOrderStep, order)

  const customFields = getCustomFields(orderStep)

  return {
    id: extOrderStep.id,
    company: order.archivedData
      ? order.archivedData.companyName
      : order.companyId === null
        ? '-'
        : order.companyId,
    name: order.name,
    loads: order.loads,
    phone: getPhone(orderStep),
    notes: getNotes(orderStep),
    customFields,
    email: getEmail(orderStep),
    pairId: getPairId(extOrderStep),
    status: getOrderStepStatus(extOrderStep, orderStep, translate),
    stopNo: getStopNumber(extOrderStep),
    address: getAddress(extOrderStep),
    orderId: !extOrderStep.isUnassigned ? extOrderStep.routeStepLabel : '-',
    barcodes: getBarcodes(extOrderStep, orderStep),
    idleTime: getIdleTime(extOrderStep, translate),
    priority: getPriority(order, translate),
    orderType: getOrderType(extOrderStep, translate),
    bannedTags: getBannedTags(orderStep),
    assignedTo: getAssignedVehicle(extOrderStep),
    recurrence: getRecurrence(recurrencesMap, order.recurrenceId),
    eligibility: order.eligibility,

    plannedTime: getPlannedTime(extOrderStep, approvedPlans),
    timeWindow1: getTimeWindow(orderStep, 0),
    timeWindow2: getTimeWindow(orderStep, 1),
    serviceTime: getServiceTime(orderStep, translate),
    requiredTags: getRequiredTags(orderStep),
    statusReason: getOrderStepStatusReason(orderStep),
    scheduleTime: getScheduleTime(extOrderStep),
    addressCheck: getAddressCheck(extOrderStep, translate),
    executedLoads: getExecutedLoads(order.loads, orderStep.trackingData.loadTrackedMap),
    requiredVehicle: getRequiredVehicle(order, vehiclesMap),
    shopifyOrderName: getShopifyOrderName(order),
    executedCustomFields: getExecutedCustomFields(
      customFields,
      orderStep.trackingData.customFieldTrackedMap,
    ),
    communicatedEtaRanges: getCommunicatedEtaRanges(
      order.id,
      orderStep.type,

      orderStepTrackingDataRegistry,
    ),
  }
}
